//@ts-nocheck
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  IconButton,
  Grid,
  Stack,
  Tooltip,
  Typography,
  Popper,
  Box,
  Paper,
  Avatar,
  Divider,
  Backdrop,
  Button,
  CircularProgress,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStylesPopper } from '../AROKMS/KTree/ViewKtreeData';
import { useSearchParams } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import { RichTextReadOnly } from 'mui-tiptap';
import { motion } from 'framer-motion';
import useExtensions from 'pages/SystemTenant/RowenaAI/RowenaDefinition/components/useExtensions';
import { useGetDisplayRowenaContent, RowenaDisplayContext } from '../Rowena/RowenaContentService';
import { Player } from '@lottiefiles/react-lottie-player';
import animationData from 'assets/lottie/ai-sparkle.json';
import { useGetMyPersonalization } from 'services/v1/Tenant/ConfigurationService';

const iconButtonStyle = {
  padding: '0px',
};

export enum ControllerNavigationDirection {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}
/**
 * StreamingRichTextReadOnly: smoothly reveals HTML content by token, animating each fragment.
 */
export function StreamingRichTextReadOnly({
  htmlContent,
  speed = 50,
  extensions = [],
  stream = true,
  onScroll = () => {},
}) {
  const [visibleLength, setVisibleLength] = useState(0);
  const [content, setContent] = useState('');

  // Reset when HTML changes
  useEffect(() => {
    setVisibleLength(0);
  }, [htmlContent]);

  // Use a fixed 60fps interval to update visibleLength in chunks
  useEffect(() => {
    // Frame duration ~16ms
    const frameMs = 16;
    // Compute how many chars per frame: ensure at least 1
    const chunkSize = speed > 0 ? Math.max(1, Math.ceil(frameMs / speed)) : 1;

    const intervalId = setInterval(() => {
      setVisibleLength((prev) => {
        const next = Math.min(htmlContent.length, prev + chunkSize);
        if (next >= htmlContent.length) clearInterval(intervalId);
        return next;
      });
    }, frameMs);

    return () => clearInterval(intervalId);
  }, [htmlContent, speed]);

  // Update visible HTML slice
  useEffect(() => {
    setContent(htmlContent.slice(0, visibleLength));
  }, [visibleLength, htmlContent]);

  if (!stream) {
    return <RichTextReadOnly content={htmlContent} extensions={extensions} />;
  }

  return <RichTextReadOnly content={content} extensions={extensions} />;
}
export function RowenaContent(
  props: {
    workFlowId?: string;
    label?: string;
    tooltip?: string;
    context?: RowenaDisplayContext;
    onVisibilityChange?: (isVisible: boolean) => void;
  } = {},
) {
  const [anchorEl, setAnchorEl] = useState<null | React.MouseEvent<HTMLElement>>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [searchParams] = useSearchParams();
  const explicitWorkFlowId = props?.workFlowId;
  const workFlowId = explicitWorkFlowId ?? searchParams.get('workFlowId');
  const label = props?.label;
  const tooltipTitle = props?.tooltip ?? 'Rowena';
  const onVisibilityChange = props?.onVisibilityChange;
  const firstLoadRef = useRef(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef<number>(0);
  const pageRef = useRef<HTMLDivElement>(null);

  const { data: allMyViewsData } = useGetMyPersonalization({ enabled: true });

  const [isReady, setIsReady] = useState(true);
  const { data: rowenaContentData, isFetching: isFetchingContent } = useGetDisplayRowenaContent(
    workFlowId,
    props?.context,
  );

  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [visited, setVisited] = useState<Set<number>>(new Set());

  const pages = useMemo(() => {
    return rowenaContentData?.data?.pages ?? [];
  }, [rowenaContentData?.data]);

  const toc = useMemo(() => {
    return rowenaContentData?.data?.toc ?? [];
  }, [rowenaContentData?.data]);

  const tocPreamble = rowenaContentData?.data?.tocPreamble ?? '';

  const hasToc = Boolean(tocPreamble) && toc.length > 0;

  useEffect(() => {
    if (pages.length === 0 || currentIndex === null) return;

    const prev = prevIndexRef.current;
    prevIndexRef.current = currentIndex;

    if (currentIndex > prev) {
      // moved forward
    } else {
      setIsReady(true);
    }
  }, [currentIndex, pages]);

  useEffect(() => {
    if (!rowenaContentData?.data) return;
    const hasTocNow = Boolean(rowenaContentData.data.tocPreamble) && (rowenaContentData.data.toc?.length ?? 0) > 0;
    // With TOC: no page selected yet — user picks from list.
    // Without TOC: default to first page.
    setCurrentIndex(hasTocNow ? null : 0);
    setVisited(new Set());
    firstLoadRef.current = true;
  }, [rowenaContentData?.data]);

  const classes = useStylesPopper();

  const extensions = useExtensions({
    placeholder: 'Add your own content here...',
  });

  const handleOnBoxIconClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.target);
  };

  const handleOnCloseModal = () => {
    firstLoadRef.current = false;
    setAnchorEl(null);
    setCurrentIndex(hasToc ? null : 0);
  };

  const jumpToPage = (pageIndex: number) => {
    firstLoadRef.current = false;
    setCurrentIndex(pageIndex);
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(pageIndex);
      return next;
    });
    requestAnimationFrame(() => {
      pageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleClosePage = () => setCurrentIndex(null);

  const isContentExist = useMemo(() => {
    if (pages.length === 0) return false;
    // When a caller passes an explicit workFlowId, skip the personalization gate —
    // static IDs (e.g. "MY_VIEW") are available everywhere the caller chooses to render this.
    if (explicitWorkFlowId) return true;
    return Boolean(allMyViewsData?.data?.rowenaWorkflowIds?.has(workFlowId));
  }, [allMyViewsData, pages, workFlowId, explicitWorkFlowId]);

  useEffect(() => {
    onVisibilityChange?.(isContentExist);
  }, [isContentExist, onVisibilityChange]);

  if (!isContentExist) return null;

  return (
    <>
      <Backdrop
        open={Boolean(anchorEl)}
        sx={{
          zIndex: 1100,
        }}
      />
      <Grid
        container
        justifyContent='center'
        alignItems='center'
        style={{ width: 'fit-content', position: 'relative' }}
      >
        <Tooltip title={tooltipTitle}>
          {label ? (
            <Button
              onClick={handleOnBoxIconClick}
              disableRipple
              sx={{
                textTransform: 'none',
                bgcolor: '#F4FBF8',
                color: '#246B57',
                border: '1px solid #CDE9DF',
                borderRadius: 2,
                minWidth: 0,
                height: 34,
                px: 1.5,
                fontFamily: '"Nunito", "Helvetica", "Arial", sans-serif',
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1,
                letterSpacing: '0.01em',
                boxShadow: '0 1px 2px rgba(36, 107, 87, 0.08)',
                transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                '& .MuiButton-startIcon': {
                  mr: 0.75,
                  ml: 0,
                },
                '& .MuiSvgIcon-root': {
                  color: '#2F9D7C',
                  fontSize: 17,
                },
                '&:hover': {
                  bgcolor: '#EAF7F2',
                  borderColor: '#9FD5C4',
                  boxShadow: '0 2px 6px rgba(36, 107, 87, 0.12)',
                },
                '&:active': {
                  bgcolor: '#DFF2EB',
                  boxShadow: 'none',
                },
                '&:focus-visible': {
                  outline: '3px solid rgba(66, 187, 147, 0.24)',
                  outlineOffset: 2,
                },
              }}
              startIcon={
                isFetchingContent ? (
                  <CircularProgress size={15} thickness={5} sx={{ color: '#2F9D7C' }} />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
            >
              {label}
            </Button>
          ) : (
            <IconButton
              onClick={handleOnBoxIconClick}
              style={{ ...iconButtonStyle }}
              sx={{
                '@keyframes rowenaBreath': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    filter: 'drop-shadow(0 0 0 rgba(66, 187, 147, 0))',
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    filter: 'drop-shadow(0 0 5px rgba(66, 187, 147, 0.45))',
                  },
                },
                // All effects live on the inner icon so the IconButton itself stays
                // at a stable bounding box — critical for Popper.js arrow positioning.
                '& .MuiSvgIcon-root': {
                  animation: 'rowenaBreath 3.5s ease-in-out infinite',
                  transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 200ms ease',
                },
                '&:hover .MuiSvgIcon-root': {
                  animation: 'none',
                  transform: 'scale(1.15) rotate(20deg)',
                  filter: 'drop-shadow(0 0 6px rgba(66, 187, 147, 0.7)) drop-shadow(0 0 14px rgba(66, 187, 147, 0.4))',
                },
                '&:active .MuiSvgIcon-root': {
                  transform: 'scale(0.94)',
                },
              }}
            >
              {isFetchingContent ? (
                <CircularProgress size={20} thickness={5} sx={{ color: '#42BB93' }} />
              ) : (
                <AutoAwesomeIcon fontSize='medium' sx={{ color: '#42BB93' }} />
              )}
            </IconButton>
          )}
        </Tooltip>
      </Grid>
      <Popper
        id='chat-popper'
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement='right-start'
        disablePortal={false}
        className={classes.popper}
        sx={{ zIndex: 1200 }}
        modifiers={[
          { name: 'flip', options: { altBoundary: true, rootBoundary: 'document', padding: 8 } },
          { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 8 } },
          { name: 'arrow', options: { element: arrowRef } },
        ]}
      >
        <span className={classes.arrow} ref={setArrowRef} />

        <Box
          component={Paper}
          elevation={2}
          sx={{
            minWidth: 380,
            maxWidth: 550,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F0F0F0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            bgcolor='#fff'
            px={2}
            py={1}
            flex='0 0 auto'
          >
            <Box display='flex' alignItems='center'>
              <AutoAwesomeIcon sx={{ mr: 1, color: '#42BB93' }} />
              <Stack direction='column' spacing={0.5}>
                <Typography variant='modal-title' fontWeight='bold' sx={{ color: '#3B4797' }}>
                  Rowena
                </Typography>
              </Stack>
            </Box>
            <IconButton size='small' onClick={handleOnCloseModal}>
              <HighlightOffRoundedIcon fontSize='small' sx={{ color: '#98A2AE', cursor: 'pointer' }} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              p: 1,
              flex: 1,
              overflowY: 'auto',
              '& .ProseMirror': {
                lineHeight: 1.6,
                '& > *:first-of-type': { mt: 0 },
                '& > *:last-child': { mb: 0 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 2,
                  mb: 0.5,
                  lineHeight: 1.3,
                },
                '& p': { my: 0 },
                '& p + p': { mt: 0.75 },
                '& ul, & ol': { pl: 2.5, my: 0.5 },
                '& li + li': { mt: 0.25 },
                '& li > p': { my: 0 },
              },
            }}
          >
            <Box display='flex' alignItems='flex-start' mb={1}>
              <Avatar sx={{ width: 32, height: 32, mr: 1, mt: 1, bgcolor: '#00B3DC' }}>RO</Avatar>
              <Box sx={{ flex: 1 }}>
                {hasToc && (
                  <Paper sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', mb: 1 }}>
                    <RichTextReadOnly content={tocPreamble} extensions={extensions} />
                    <Box component='ul' sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                      {toc.map((entry) => {
                        const isActive = entry.pageIndex === currentIndex;
                        const isVisited = visited.has(entry.pageIndex);
                        return (
                          <Box component='li' key={entry.pageIndex} sx={{ mb: 0.25 }}>
                            <Box
                              component='button'
                              type='button'
                              onClick={() => jumpToPage(entry.pageIndex)}
                              sx={{
                                background: 'none',
                                bgcolor: isActive ? 'rgba(66, 187, 147, 0.10)' : 'transparent',
                                border: 'none',
                                borderLeft: isActive ? '3px solid #42BB93' : '3px solid transparent',
                                pl: 1,
                                py: 0.25,
                                width: '100%',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                fontSize: '0.95rem',
                                fontStyle: 'italic',
                                textDecoration: 'underline',
                                textAlign: 'left',
                                color: isActive ? '#42BB93' : isVisited ? '#94A3B8' : '#475569',
                                fontWeight: isActive ? 600 : 400,
                                transition: 'background-color 120ms, color 120ms',
                                '&:hover': {
                                  color: '#42BB93',
                                  bgcolor: 'rgba(66, 187, 147, 0.05)',
                                },
                              }}
                            >
                              {entry.title}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                )}

                {currentIndex !== null && pages[currentIndex] !== undefined && (
                  <motion.div
                    ref={pageRef}
                    key={currentIndex}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    style={{ scrollMarginTop: 8 }}
                  >
                    <Paper sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
                      {hasToc && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.75,
                          }}
                        >
                          <Button
                            size='small'
                            variant='text'
                            startIcon={<KeyboardArrowUpIcon />}
                            onClick={handleClosePage}
                            sx={{
                              color: '#64748B',
                              textTransform: 'none',
                              minWidth: 0,
                              px: 0.5,
                              '& .MuiButton-startIcon': { mr: 0.25 },
                              '&:hover': {
                                color: '#42BB93',
                                bgcolor: 'rgba(66, 187, 147, 0.05)',
                              },
                            }}
                          >
                            Back to contents
                          </Button>
                          {toc.length > 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box sx={{ display: 'flex', gap: '3px' }}>
                                {toc.map((_, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor:
                                        i === currentIndex
                                          ? '#42BB93'
                                          : i < (currentIndex ?? -1)
                                          ? '#A7E0C8'
                                          : '#E2E8F0',
                                      transition: 'background-color 150ms',
                                    }}
                                  />
                                ))}
                              </Box>
                              <Typography variant='caption' sx={{ color: '#98A2AE', fontWeight: 500, lineHeight: 1 }}>
                                {(currentIndex ?? 0) + 1} of {toc.length}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {!isReady ? (
                        <Stack flex={1} width='100%' justifyContent='center' alignItems='center' sx={{ height: 200 }}>
                          <Player src={animationData} autoplay loop style={{ height: 60 }} />
                          <Typography variant='body2' sx={{ color: '#98A2AE', mt: 1 }}>
                            Preparing insights from your data …
                          </Typography>
                        </Stack>
                      ) : (
                        <StreamingRichTextReadOnly
                          speed={1}
                          htmlContent={pages[currentIndex]}
                          extensions={extensions}
                          sx={{ flex: 1, overflowY: 'auto' }}
                          stream={firstLoadRef.current && currentIndex === 0 && !hasToc}
                        />
                      )}
                      {hasToc && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 1,
                            gap: 1,
                          }}
                        >
                          {currentIndex > 0 && toc[currentIndex - 1] ? (
                            <Button
                              size='small'
                              variant='text'
                              startIcon={<WestIcon />}
                              onClick={() => jumpToPage(currentIndex - 1)}
                              sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                minWidth: 0,
                                '& .MuiButton-startIcon': { mr: 0.5 },
                              }}
                            >
                              Previous
                            </Button>
                          ) : (
                            <Box />
                          )}
                          {currentIndex < pages.length - 1 && toc[currentIndex + 1] ? (
                            <Button
                              size='small'
                              variant='text'
                              endIcon={<EastIcon />}
                              onClick={() => jumpToPage(currentIndex + 1)}
                              sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                minWidth: 0,
                                maxWidth: '70%',
                                '& .MuiButton-endIcon': { ml: 0.5 },
                              }}
                            >
                              <Box
                                component='span'
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Continue &rarr; {toc[currentIndex + 1].title}
                              </Box>
                            </Button>
                          ) : (
                            <Box />
                          )}
                        </Box>
                      )}
                      {!hasToc && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            size='small'
                            variant='text'
                            color='info'
                            startIcon={<WestIcon />}
                            sx={{
                              mr: 1,
                              color: '#64748B',
                              textTransform: 'none',
                              visibility: currentIndex === 0 || !isReady ? 'hidden' : 'visible',
                            }}
                            onClick={() => setCurrentIndex((i) => (i ?? 0) - 1)}
                          >
                            Prev
                          </Button>
                          {isReady && currentIndex < pages.length - 1 && (
                            <Button
                              size='small'
                              variant='text'
                              onClick={() => setCurrentIndex((i) => (i ?? 0) + 1)}
                              endIcon={<EastIcon />}
                              sx={{ color: '#64748B', textTransform: 'none' }}
                            >
                              More
                            </Button>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </motion.div>
                )}
              </Box>
            </Box>
            <div ref={messagesEndRef} />
          </Box>
        </Box>
      </Popper>
    </>
  );
}
